export const MAX_GEOTIFF_BYTES = 512 * 1024 * 1024;

export async function readGeoTiff(file) {
  if (file.size > MAX_GEOTIFF_BYTES) throw new Error('GeoTIFF files can be up to 512 MB per file.');
  const [{ fromBlob, fromCustomClient }, { default: RasterLayer }] = await Promise.all([
    import('geotiff'), import('georaster-layer-for-leaflet'),
  ]);
  const tiff = file.getUrl ? await fromCustomClient({
    async request({headers,signal}={}) {
      const response=await fetch(await file.getUrl(),{headers,signal:signal||file.signal});
      return {ok:response.ok,status:response.status,getHeader:name=>response.headers.get(name),getData:()=>response.arrayBuffer()};
    },
  }, {allowFullFile:false}, file.signal) : await fromBlob(file);
  const image = await tiff.getImage();
  const keys = image.getGeoKeys() || {};
  const projection = keys.ProjectedCSTypeGeoKey || keys.GeographicTypeGeoKey;
  if (!projection || projection === 32767) throw new Error('This TIFF has no supported EPSG coordinate system. Export a GeoTIFF using WGS 84 or WGS 84 / UTM.');
  const fd = image.getFileDirectory();
  if (fd.ModelTransformation && (fd.ModelTransformation[1] !== 0 || fd.ModelTransformation[4] !== 0)) throw new Error('Rotated GeoTIFF grids are not supported. Export a north-up orthomosaic.');
  const [dx, dy] = image.getResolution();
  const [xmin, ymax] = image.getOrigin();
  if (!(dx > 0 && dy < 0)) throw new Error('Export a north-up GeoTIFF orthomosaic.');
  const bands = image.getSamplesPerPixel();
  if (![1, 3, 4].includes(bands) || fd.BitsPerSample.some(bits => bits !== 8) || ![1, 2].includes(fd.PhotometricInterpretation)) {
    throw new Error('Export an 8-bit RGB or grayscale GeoTIFF for map display.');
  }
  const noDataValue = image.getGDALNoData();
  const georaster = { projection, xmin, ymax, xmax: xmin + image.getWidth() * dx,
    ymin: ymax + image.getHeight() * dy, width: image.getWidth(), height: image.getHeight(),
    pixelWidth: dx, pixelHeight: -dy, numberOfRasters: bands, noDataValue, sourceType: 'url',
    async getValues({ left, top, right, bottom, width, height, resampleMethod }) {
      const values = await tiff.readRasters({ window: [left, top, right, bottom], width, height, resampleMethod: resampleMethod || 'nearest' });
      return Array.from(values, band => Array.from({ length: height }, (_, row) => band.subarray(row * width, (row + 1) * width)));
    },
  };
  const pixelValuesToColorFn = values => {
    if (values.some(value => !Number.isFinite(value)) || (noDataValue !== null && values.slice(0, bands === 1 ? 1 : 3).every(value => value === noDataValue))) return null;
    if (bands === 1) return `rgb(${values[0]},${values[0]},${values[0]})`;
    return `rgba(${values[0]},${values[1]},${values[2]},${bands === 4 ? values[3] / 255 : 1})`;
  };
  let bounds;
  try {
    const probe = new RasterLayer({ georaster, pixelValuesToColorFn, resolution: 128 });
    bounds = probe.getBounds();
    if (!bounds.isValid() || ![bounds.getSouth(), bounds.getNorth(), bounds.getWest(), bounds.getEast()].every(Number.isFinite)
      || bounds.getSouth() < -85.051129 || bounds.getNorth() > 85.051129 || bounds.getWest() < -180 || bounds.getEast() > 180) throw new Error('Invalid bounds');
  } catch (_) {
    throw new Error('The GeoTIFF coordinate system cannot be displayed. Export using WGS 84 or the appropriate WGS 84 / UTM zone.');
  }
  return { id: `geotiff-${Date.now()}-${Math.random()}`, name: file.name, georaster, RasterLayer, pixelValuesToColorFn,
    bounds: [[bounds.getSouth(), bounds.getWest()], [bounds.getNorth(), bounds.getEast()]],
    data: { type: 'FeatureCollection', features: [] }, rasters: [], opacity: 1, visible: true };
}
